import { ChevronRight, Copy, QrCode, Share2 } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { BadgePill } from '@/components/BadgePill';
import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import { formatMoney } from '@/lib/money';

/**
 * A referral program the user can share — one per referable deal, plus the
 * app-level invite. v5 (2026-09-17): a chooser, not a pitch. Full rationale
 * and the drift log live in docs/screens/referrals.md § v5.
 *
 * Three things here are load-bearing, not taste:
 *
 *   1. The CARD is not a Pressable. Wrapping it in
 *      accessibilityRole="button" made iOS collapse the nested
 *      Share/Copy/QR buttons and VoiceOver users could not share at all.
 *      The header ROW is the button; the action buttons are its siblings.
 *   2. The share CTA is Deep Green, matching Jason's comp — Jeff's call on
 *      2026-09-17 ("mirror the mocks"). This contradicts CLAUDE.md's
 *      "Primary CTA = electricGreen … NOT deep-green-bg buttons", and is
 *      also what marketpryce.com does, where Electric Green appears zero
 *      times. If the brand rule wins later, this is a one-word change.
 *   3. The tile is a Frost plate holding the real mark, not a tinted swatch
 *      — Cool Blue beside Electric Green is a forbidden pair (brand kit p.11).
 */
export interface ProgramCardProps {
  /** Brand mark for the tile — a <BrandLogo> for deals, monogram for the invite. */
  tile: ReactNode;
  /** The program's name, as the server spells it ("SoFi Checking"). */
  name: string;
  /**
   * Live-boost marker ("2x") beside the name. Server-owned label: a killed
   * bonus means no label means no pill. Deals only; the invite is flat.
   */
  badge?: string | null;
  /** What the sharer earns, in cents. */
  referrerCents: number;
  /**
   * What their friend gets, in cents. Zero renders as "You get $X" alone —
   * the invite program is referrer-only until we actually pay the invitee,
   * and a card must never promise money that won't arrive.
   */
  userCents: number;
  /**
   * Availability under the trade line. Deals pass the same bar the Deals
   * tab draws (`<DealAvailabilityBar>`), which renders nothing for an
   * uncapped deal — so an uncapped card matches the mock exactly and a
   * capped one still says how many spots are left before asking for a
   * share.
   */
  availability?: ReactNode;
  /** Primary CTA label — the share action. */
  shareLabel: string;
  onShare: () => void;
  onCopy: () => void;
  onShowQr: () => void;
  /** Opens the program's detail screen — the header row's action. */
  onOpenDetail: () => void;
  /** Deal is full — share actions are disabled and the CTA says so. */
  disabled?: boolean;
  /** Share action in flight. */
  sharing?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export function ProgramCard({
  tile,
  name,
  badge,
  referrerCents,
  userCents,
  availability,
  shareLabel,
  onShare,
  onCopy,
  onShowQr,
  onOpenDetail,
  disabled = false,
  sharing = false,
  style,
  testID,
}: ProgramCardProps) {
  // iOS reads a labelled button as ONE element and skips its children, so
  // everything on the header row has to be in the label: the name, a live
  // bonus, and both sides of the trade. Without them a VoiceOver user could
  // not compare programs without opening each one (v4 read the amounts as
  // separate elements). The chevron is the only visual cue that the row
  // navigates, so the label also says where it goes.
  const trade = `You get ${formatMoney(referrerCents)}${
    userCents > 0 ? `, they get ${formatMoney(userCents)}` : ''
  }`;
  const headerLabel = `${name}${badge ? `, ${badge} bonus` : ''}. ${trade}. See details.`;

  return (
    <View style={[styles.card, style]} testID={testID}>
      <Pressable
        onPress={onOpenDetail}
        accessibilityRole="button"
        accessibilityLabel={headerLabel}
        style={({ pressed }) => [styles.headerRow, pressed && styles.headerRowPressed]}
        testID={testID ? `${testID}-detail` : undefined}
      >
        {tile}
        <View style={styles.headerText}>
          <View style={styles.nameRow}>
            {/* Two lines, not one: the offer name is the only thing telling
                two SoFi cards apart, and "SoFi Active Investing…" hides the
                part that differs. */}
            <Text style={styles.name} numberOfLines={2}>
              {name}
            </Text>
            <BadgePill label={badge} testID={testID ? `${testID}-badge` : undefined} />
          </View>
          {/* Both sides of the trade on one line, same slot on every card:
              sibling cards showing different metric types read as arbitrary
              and can't be compared. Plain bold numerals rather than
              <MoneyDisplay> because this is a sentence — Druk Wide's
              scoreboard numerals are for figures that stand alone. */}
          <Text style={styles.trade} numberOfLines={2}>
            You get <Text style={styles.tradeAmount}>{formatMoney(referrerCents)}</Text>
            {/* Single spaces around the rule: the column is ~190pt after
                the tile, the chevron and two gaps. Two lines allowed rather
                than one — a wide pair ("$100 | $150") wraps instead of
                truncating the friend's amount to "$1…". */}
            {userCents > 0 ? (
              <>
                {' | '}
                They get <Text style={styles.tradeAmount}>{formatMoney(userCents)}</Text>
              </>
            ) : null}
          </Text>
        </View>
        <ChevronRight size={18} color={colors.textMuted} strokeWidth={2.25} />
      </Pressable>

      {availability}

      <Button
        variant="dark"
        size="base"
        fullWidth
        label={disabled ? 'Full this month' : shareLabel}
        onPress={onShare}
        disabled={disabled}
        loading={sharing}
        leadingIcon={disabled ? undefined : Share2}
        testID={testID ? `${testID}-share` : undefined}
      />

      <View style={styles.quickRow}>
        <Button
          variant="outline"
          size="base"
          label="Copy link"
          onPress={onCopy}
          disabled={disabled}
          leadingIcon={Copy}
          style={styles.quickButton}
          testID={testID ? `${testID}-copy` : undefined}
        />
        <Button
          variant="outline"
          size="base"
          label="QR code"
          onPress={onShowQr}
          disabled={disabled}
          leadingIcon={QrCode}
          style={styles.quickButton}
          testID={testID ? `${testID}-qr` : undefined}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: spacing.lg,
    // radii.lg (20) is the handoff's mp-card radius. The 28 override belongs
    // to hero cards only.
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadows.card,
    gap: spacing.md,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    // The row is the card's navigation target, so it carries the 44pt
    // minimum itself rather than relying on the 48pt tile to supply it.
    minHeight: 48,
  },
  headerRowPressed: { opacity: 0.6 },
  headerText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    // A bonus badge is admin copy of any length ("2x", "$40 PER REFERRAL").
    // When it can't sit beside the name it drops whole onto its own line
    // under it, instead of pushing off the card's edge or truncating to
    // "$40 PER R…" — the bug the old index shipped with.
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  name: {
    flexShrink: 1,
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.lg,
    lineHeight: 24,
    color: colors.deepGreen,
  },
  trade: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    lineHeight: 22,
    color: colors.text,
  },
  tradeAmount: {
    fontFamily: fonts.bodyBold,
    // The app's <Text> applies its 16/22 body style before `style`, so a
    // nested amount without its own size renders 2pt larger than the
    // sentence around it, with a taller line box — which is what made the
    // line wrap and rag in the first place.
    fontSize: fontSizes.sm,
    lineHeight: 20,
    color: colors.deepGreen,
  },
  quickRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  quickButton: { flex: 1 },
});
